import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { EvacuationGroupSettings, EvacuationSiteSettings } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useSite } from '../../lib/site';
import { useSettingsDraft } from '../../lib/useSettingsDraft';
import { Alert, Button, Card, CheckboxField, SelectField, Spinner, TextField } from '../../components/ui';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { useToast, useToastOnSuccess } from '../../components/Toast';
import { EmailListInput } from '../../components/phase2';
import type { SettingsCardProps } from '../../components/SettingsCards';
import { evacKey, usePoints } from './api';
import { MarshalPicker } from './Marshals';
import { MusterPoints } from './MusterPoints';

type SiteSettings = ReturnType<typeof EvacuationSiteSettings.parse>;
type GroupSettings = ReturnType<typeof EvacuationGroupSettings.parse>;

export const PIN_PATTERN = /^\d{4,8}$/;

/** Kiosk evacuation PIN: set or remove. It is write-only; the server keeps only a hash. */
function KioskPin({ siteId, canEdit }: { siteId: string; canEdit: boolean }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [pin, setPin] = useState('');
  const save = useMutation({
    mutationFn: (value: string | null) => api.put(`/evacuation/sites/${siteId}/kiosk-pin`, { body: { pin: value } }),
    onSuccess: (_r, value) => {
      setPin('');
      toast(value === null ? t('evac.pin.cleared') : t('evac.pin.saved'));
      void qc.invalidateQueries({ queryKey: evacKey });
    },
  });
  const bad = pin !== '' && !PIN_PATTERN.test(pin);
  return (
    <div className="space-y-2">
      <ErrorLine error={save.error} />
      <div className="flex flex-wrap items-end gap-2">
        <TextField
          label={t('evac.pin.label')}
          type="password"
          inputMode="numeric"
          autoComplete="off"
          maxLength={8}
          disabled={!canEdit}
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          className="w-48"
          {...(bad ? { error: t('evac.pin.invalid') } : {})}
        />
        {canEdit ? (
          <>
            <Button disabled={!PIN_PATTERN.test(pin) || save.isPending} onClick={() => save.mutate(pin)}>
              {t('evac.pin.set')}
            </Button>
            <Button disabled={save.isPending} onClick={() => save.mutate(null)}>
              {t('evac.pin.clear')}
            </Button>
          </>
        ) : null}
      </div>
      <p className="text-xs text-slate-600">{t('evac.pin.hint')}</p>
    </div>
  );
}

/** Site card: muster points, then the site settings (default point, kiosk start, PIN, marshals, contacts). */
export function EvacuationSiteCard({ siteId, canEdit }: { siteId: string; canEdit: boolean }) {
  const s = useSettingsDraft(`/sites/${siteId}/settings`, 'evacuation', EvacuationSiteSettings);
  const points = usePoints(siteId);
  useToastOnSuccess(s.save.isSuccess, t('common.saved'));
  if (s.q.isError) return <ErrorPanel error={s.q.error} onRetry={() => void s.q.refetch()} />;
  const d: SiteSettings | null = s.draft;
  if (!d) return <Spinner />;
  const set = (patch: Partial<SiteSettings>) => s.setDraft({ ...d, ...patch });
  const active = (points.data ?? []).filter((p) => p.active);

  return (
    <div className="space-y-6">
      <MusterPoints siteId={siteId} canEdit={canEdit} />
      <Card
        title={t('evac.card.title')}
        description={t('evac.card.description')}
        footer={
          canEdit ? (
            <Button variant="primary" disabled={!s.changed || !s.valid || s.save.isPending} onClick={() => s.save.mutate()}>
              {t('common.save')}
            </Button>
          ) : null
        }
      >
        <div className="space-y-5">
          <ErrorLine error={s.save.error} />
          <SelectField
            label={t('evac.settings.defaultPoint')}
            hint={t('evac.settings.defaultPointHint')}
            disabled={!canEdit}
            value={d.defaultPointId ?? ''}
            onChange={(e) => set({ defaultPointId: e.target.value || null })}
          >
            <option value="">{t('evac.settings.noDefault')}</option>
            {active.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </SelectField>
          <SelectField
            label={t('evac.settings.kioskStart')}
            hint={t('evac.settings.kioskStartHint')}
            disabled={!canEdit}
            value={d.kioskStart}
            onChange={(e) => set({ kioskStart: e.target.value as SiteSettings['kioskStart'] })}
          >
            {(['off', 'pin', 'open'] as const).map((m) => (
              <option key={m} value={m}>
                {t(`evac.settings.kioskStart.${m}`)}
              </option>
            ))}
          </SelectField>
          {d.kioskStart === 'pin' ? <KioskPin siteId={siteId} canEdit={canEdit} /> : null}
          <MarshalPicker ids={d.marshalMemberIds} disabled={!canEdit} onChange={(marshalMemberIds) => set({ marshalMemberIds })} />
          <EmailListInput label={t('evac.contacts.label')} hint={t('evac.contacts.hint')} value={d.contactEmails} disabled={!canEdit} max={20} onChange={(contactEmails) => set({ contactEmails })} />
        </div>
      </Card>
    </div>
  );
}

function GroupPointRow({ siteId, siteName, value, disabled, onChange }: { siteId: string; siteName: string; value: string; disabled: boolean; onChange: (pointId: string) => void }) {
  const points = usePoints(siteId);
  return (
    <SelectField label={siteName} disabled={disabled} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{t('evac.group.siteDefault')}</option>
      {(points.data ?? [])
        .filter((p) => p.active)
        .map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
    </SelectField>
  );
}

/** Group card: show on the list, evacuate with the host, and a default muster point per site. */
export function EvacuationGroupCard({ groupId, canEdit }: { groupId: string; canEdit: boolean }) {
  const s = useSettingsDraft(`/groups/${groupId}/settings`, 'evacuation', EvacuationGroupSettings);
  const { sites } = useSite();
  useToastOnSuccess(s.save.isSuccess, t('common.saved'));
  if (s.q.isError) return <ErrorPanel error={s.q.error} onRetry={() => void s.q.refetch()} />;
  const d: GroupSettings | null = s.draft;
  if (!d) return <Spinner />;
  const set = (patch: Partial<GroupSettings>) => s.setDraft({ ...d, ...patch });
  const usable = sites.filter((x) => !x.isRemote);
  const setPoint = (siteId: string, pointId: string) => {
    const next = { ...d.defaultPoints };
    if (pointId) next[siteId] = pointId;
    else delete next[siteId];
    set({ defaultPoints: next });
  };
  return (
    <Card
      title={t('evac.group.title')}
      description={t('evac.group.description')}
      footer={
        canEdit ? (
          <Button variant="primary" disabled={!s.changed || !s.valid || s.save.isPending} onClick={() => s.save.mutate()}>
            {t('common.save')}
          </Button>
        ) : null
      }
    >
      <div className="space-y-4">
        <ErrorLine error={s.save.error} />
        <CheckboxField label={t('evac.group.showOnList')} hint={t('evac.group.showOnListHint')} checked={d.showOnList} disabled={!canEdit} onChange={(e) => set({ showOnList: e.target.checked })} />
        <CheckboxField label={t('evac.group.withHost')} hint={t('evac.group.withHostHint')} checked={d.evacuateWithHost} disabled={!canEdit} onChange={(e) => set({ evacuateWithHost: e.target.checked })} />
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-slate-800">{t('evac.group.defaultPoints')}</legend>
          {usable.length === 0 ? <Alert kind="info">{t('evac.group.noSites')}</Alert> : null}
          {usable.map((x) => (
            <GroupPointRow key={x.id} siteId={x.id} siteName={x.name} value={d.defaultPoints[x.id] ?? ''} disabled={!canEdit} onChange={(p) => setPoint(x.id, p)} />
          ))}
        </fieldset>
      </div>
    </Card>
  );
}

/** Registered for module key `evacuation`: the settings page draws the site or group version. */
export function EvacuationSettingsCard({ scope, scopeId, canEdit }: SettingsCardProps) {
  return scope === 'sites' ? <EvacuationSiteCard siteId={scopeId} canEdit={canEdit} /> : <EvacuationGroupCard groupId={scopeId} canEdit={canEdit} />;
}
